/**
 * frontend/src/pages/Register.jsx
 * =================================
 * Invite-only registration page.
 *
 * Flow:
 *   1. Extract ?invite= token from URL
 *   2. Validate it against GET /api/auth/invite/validate
 *   3. Render form (email pre-filled if invite specifies one)
 *   4. On submit: verify Turnstile + POST /api/auth/register-invite
 *   5. Redirect to /login with success banner
 */

import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { authApi } from '../lib/api'

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || ''

export function Register() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const inviteToken = searchParams.get('invite') || ''

  // Invite validation state
  const [inviteStatus, setInviteStatus] = useState('loading') // loading | valid | invalid
  const [inviteError, setInviteError] = useState(null)
  const [wineryName, setWineryName] = useState('')
  const [lockedEmail, setLockedEmail] = useState(null)

  // Form state
  const [email, setEmail] = useState('')
  const [fullName, setFullName] = useState('')
  const [password, setPassword] = useState('')
  const [passwordConfirm, setPasswordConfirm] = useState('')
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)

  // Turnstile
  const [turnstileToken, setTurnstileToken] = useState(null)
  const turnstileRef = useRef(null)
  const widgetIdRef = useRef(null)

  // ── Validate invite on mount ───────────────────────────────────────────────

  useEffect(() => {
    if (!inviteToken) {
      setInviteStatus('invalid')
      setInviteError('A valid invite link is required to register.')
      return
    }

    authApi.validateInvite(inviteToken)
      .then(({ data }) => {
        if (data.valid) {
          setInviteStatus('valid')
          setWineryName(data.winery_name || '')
          if (data.email) {
            setLockedEmail(data.email)
            setEmail(data.email)
          }
        } else {
          setInviteStatus('invalid')
          setInviteError(data.reason || 'Invalid invite.')
        }
      })
      .catch(() => {
        setInviteStatus('invalid')
        setInviteError('Unable to validate invite. Please try again.')
      })
  }, [inviteToken])

  // ── Render Turnstile widget when form is ready ─────────────────────────────

  useEffect(() => {
    if (inviteStatus !== 'valid' || !TURNSTILE_SITE_KEY) return
    if (widgetIdRef.current !== null) return

    const interval = setInterval(() => {
      if (window.turnstile && turnstileRef.current) {
        clearInterval(interval)
        widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          theme: 'dark',
          callback: (token) => setTurnstileToken(token),
          'expired-callback': () => setTurnstileToken(null),
        })
      }
    }, 100)

    return () => clearInterval(interval)
  }, [inviteStatus])

  // ── Client-side password validation ────────────────────────────────────────

  function validatePassword() {
    if (password.length < 12) return 'Password must be at least 12 characters'
    if (!/[A-Z]/.test(password)) return 'Must contain an uppercase letter'
    if (!/[a-z]/.test(password)) return 'Must contain a lowercase letter'
    if (!/[0-9]/.test(password)) return 'Must contain a digit'
    if (password !== passwordConfirm) return 'Passwords do not match'
    return null
  }

  // ── Submit ─────────────────────────────────────────────────────────────────

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)

    const pwError = validatePassword()
    if (pwError) { setError(pwError); return }

    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      setError('Please complete the verification challenge.')
      return
    }

    setLoading(true)
    try {
      await authApi.registerInvite({
        invite_token: inviteToken,
        email,
        password,
        password_confirm: passwordConfirm,
        full_name: fullName || undefined,
        turnstile_token: turnstileToken || 'dev-bypass',
      })
      navigate('/login', { state: { registered: true } })
    } catch (err) {
      const detail = err.response?.data?.detail
      if (Array.isArray(detail)) {
        setError(detail.map((d) => d.msg).join('. '))
      } else {
        setError(detail || 'Registration failed. Please try again.')
      }
      // Reset Turnstile on failure
      if (window.turnstile && widgetIdRef.current !== null) {
        window.turnstile.reset(widgetIdRef.current)
        setTurnstileToken(null)
      }
    } finally {
      setLoading(false)
    }
  }

  // ── Styles (matching Login.jsx) ────────────────────────────────────────────

  const inputCls =
    'w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-violet-500 text-sm'

  // ── Invalid invite view ────────────────────────────────────────────────────

  if (inviteStatus === 'loading') {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center px-4">
        <p className="text-gray-400 text-sm">Validating invite...</p>
      </div>
    )
  }

  if (inviteStatus === 'invalid') {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center px-4">
        <div className="w-full max-w-sm text-center">
          <div className="text-4xl mb-3">🍷</div>
          <h1 className="text-2xl font-bold text-white mb-2">Chekn</h1>
          <div className="bg-red-900/40 border border-red-800 text-red-300 text-sm rounded-lg px-4 py-3 mb-6">
            {inviteError}
          </div>
          <a
            href="/login"
            className="text-violet-400 hover:text-violet-300 text-sm transition-colors"
          >
            Go to sign in
          </a>
        </div>
      </div>
    )
  }

  // ── Registration form ──────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="text-4xl mb-3">🍷</div>
          <h1 className="text-2xl font-bold text-white">Chekn</h1>
          <p className="text-gray-500 text-sm mt-1">
            {wineryName ? `Join ${wineryName}` : 'Create your account'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-red-900/40 border border-red-800 text-red-300 text-sm rounded-lg px-4 py-3">
              {error}
            </div>
          )}

          <input
            type="email"
            required
            className={inputCls}
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={!!lockedEmail}
            autoComplete="email"
          />

          <input
            type="text"
            className={inputCls}
            placeholder="Full name (optional)"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            autoComplete="name"
          />

          <div>
            <input
              type="password"
              required
              className={inputCls}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              minLength={12}
            />
            <p className="text-xs text-gray-600 mt-1 px-1">
              12+ characters, uppercase, lowercase, and a digit
            </p>
          </div>

          <input
            type="password"
            required
            className={inputCls}
            placeholder="Confirm password"
            value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(e.target.value)}
            autoComplete="new-password"
            minLength={12}
          />

          {/* Cloudflare Turnstile widget */}
          {TURNSTILE_SITE_KEY && (
            <div ref={turnstileRef} className="flex justify-center" />
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-violet-700 hover:bg-violet-600 disabled:opacity-50 text-white font-semibold py-3 rounded-lg transition-colors"
          >
            {loading ? 'Creating account...' : 'Create Account'}
          </button>
        </form>

        <p className="text-center text-gray-600 text-xs mt-6">
          Already have an account?{' '}
          <a href="/login" className="text-violet-400 hover:text-violet-300 transition-colors">
            Sign in
          </a>
        </p>
      </div>
    </div>
  )
}

export default Register
